import React from 'react';
import { EditorView } from '@components/BlockNote/EditorView';
import { Event as EventIcon, Launch as LaunchIcon } from '@mui/icons-material';
import {
  Box,
  Button,
  Card,
  CardContent,
  Divider,
  IconButton,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import { format } from 'date-fns';

export interface LastEncounterData {
  past_visit_summary?: string;
  latest_encounter?: {
    scheduled_for?: string;
    created_at?: string;
    start_time?: string;
    note_id?: string;
    outputs?: {
      type: string;
      encounter_document_id: string;
    }[];
  };
}

interface PatientLastEncounterCardProps {
  data: LastEncounterData | null;
  isLoading?: boolean;
  onViewNote?: (noteId: string) => void;
}

export const PatientLastEncounterCard: React.FC<PatientLastEncounterCardProps> = ({
  data,
  isLoading = false,
  onViewNote,
}) => {
  const encounterDate = React.useMemo(() => {
    if (!data?.latest_encounter) return null;

    const date =
      data.latest_encounter.scheduled_for ||
      data.latest_encounter.start_time ||
      data.latest_encounter.created_at;

    return date ? format(new Date(date), 'MMM d, yyyy - h:mm a') : null;
  }, [data]);

  const noteId = React.useMemo(() => {
    if (!data?.latest_encounter) return null;

    return (
      data.latest_encounter.note_id ||
      data.latest_encounter.outputs?.find(output => output.type === 'note')
        ?.encounter_document_id ||
      null
    );
  }, [data]);

  return (
    <Card
      sx={{
        width: '100%',
        minWidth: 400,
        maxWidth: 600,
        display: 'flex',
        flexDirection: 'column',
        borderRadius: 2,
        border: '1px solid',
        borderColor: 'borderColors.primary',
        boxShadow: 'none',
        overflow: 'hidden',
        transition: 'all 0.2s ease-in-out',
        bgcolor: 'background.paper',
        '&:hover': {
          boxShadow: 3,
          borderColor: 'borderColors.brand',
        },
      }}
    >
      {/* Header Section */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'flex-start',
          gap: 1,
          p: 1.5,
          pb: 1,
          bgcolor: 'backgroundColors.secondary',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: 28,
            height: 28,
            borderRadius: 1,
            bgcolor: 'primary.main',
            color: 'textColors.brandContrast',
            flexShrink: 0,
          }}
        >
          <EventIcon sx={{ fontSize: 16 }} />
        </Box>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography
            variant="subtitle2"
            sx={{
              fontWeight: 600,
              fontSize: '0.8125rem',
              lineHeight: 1.25,
              color: 'textColors.darkText',
              mb: 0.25,
            }}
          >
            Last Encounter
          </Typography>
          {encounterDate && (
            <Typography
              variant="caption"
              sx={{ color: 'textColors.lightText', fontSize: '0.6875rem' }}
            >
              {encounterDate}
            </Typography>
          )}
        </Box>
        {noteId && onViewNote && (
          <Tooltip title="Open Note">
            <IconButton size="small" onClick={() => onViewNote(noteId)} sx={{ ml: 'auto' }}>
              <LaunchIcon sx={{ fontSize: 16 }} />
            </IconButton>
          </Tooltip>
        )}
      </Box>

      <Divider sx={{ borderColor: 'borderColors.primary' }} />

      {/* Content Section */}
      <CardContent
        sx={{
          p: 1.5,
          pt: 1,
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          overflow: 'auto',
          maxHeight: 400,
        }}
      >
        {isLoading ? (
          <Box sx={{ p: 2, textAlign: 'center' }}>
            <Typography variant="body2" color="text.secondary">
              Loading last encounter...
            </Typography>
          </Box>
        ) : !data?.latest_encounter ? (
          <Box sx={{ p: 2, textAlign: 'center' }}>
            <Typography variant="body2" color="text.secondary">
              No previous encounters recorded for this patient.
            </Typography>
          </Box>
        ) : (
          <Stack spacing={1.5}>
            {/* Past Visit Summary */}
            {data.past_visit_summary ? (
              <Box>
                <Typography
                  variant="overline"
                  sx={{
                    fontWeight: 600,
                    color: 'textColors.lightText',
                    fontSize: '0.625rem',
                    letterSpacing: '0.5px',
                    textTransform: 'uppercase',
                    display: 'block',
                    mb: 0.75,
                  }}
                >
                  Visit Summary
                </Typography>
                <Box
                  sx={{
                    p: 1,
                    borderRadius: 1,
                    bgcolor: 'backgroundColors.tertiary',
                    border: '1px solid',
                    borderColor: 'borderColors.lightprimary',
                  }}
                >
                  <EditorView
                    markdown={data.past_visit_summary}
                    editable={false}
                    sideMenu={false}
                    formattingToolbar={false}
                    sx={{
                      '& .bn-editor': {
                        padding: 0,
                      },
                      '& .bn-block-group': {
                        padding: 0,
                      },
                      '& .bn-block-outer': {
                        marginBlock: 0,
                      },
                      '& p': {
                        fontSize: '0.75rem',
                        lineHeight: 1.5,
                        color: 'textColors.darkText',
                      },
                    }}
                  />
                </Box>
              </Box>
            ) : (
              <Box sx={{ p: 1 }}>
                <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
                  No summary available for the last visit.
                </Typography>
              </Box>
            )}

            {/* View Note Button */}
            {noteId && onViewNote && (
              <Box sx={{ mt: 1, display: 'flex', justifyContent: 'center' }}>
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<LaunchIcon sx={{ fontSize: 14 }} />}
                  onClick={() => onViewNote(noteId)}
                  sx={{ fontSize: '0.75rem' }}
                >
                  View Full Note
                </Button>
              </Box>
            )}
          </Stack>
        )}
      </CardContent>
    </Card>
  );
};
